import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Monitor, Download, ArrowRight, ExternalLink } from "lucide-react";
import { useData } from "@/contexts/DataContext";

const SKIP_KEY = "dh_ultimate_skipped_install";
const DESKTOP_DOWNLOAD_URL = "https://dailyhisaab.com/desktop";

export function InstallDesktopGate({ children }: { children: React.ReactNode }) {
  const { isUltimateOwnerMobile, devices } = useData();
  const [skipped, setSkipped] = useState<boolean>(() =>
    typeof window !== "undefined" && localStorage.getItem(SKIP_KEY) === "true"
  );

  const desktopCount = devices?.active?.filter((d) => d.type === "desktop").length || 0;
  const showGate = isUltimateOwnerMobile && desktopCount === 0 && !skipped;

  useEffect(() => {
    if (desktopCount > 0) {
      // Once desktop is connected, clear the skip flag so re-uninstalls re-prompt.
      try {
        localStorage.removeItem(SKIP_KEY);
      } catch {}
    }
  }, [desktopCount]);

  const handleSkip = () => {
    try {
      localStorage.setItem(SKIP_KEY, "true");
    } catch {}
    setSkipped(true);
  };

  if (!showGate) return <>{children}</>;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[150] bg-[#1A1614] flex flex-col px-6 pt-12 pb-8 overflow-y-auto"
    >
      <div className="flex justify-center mb-6">
        <div className="rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 px-3 py-1.5 flex items-center gap-1.5">
          <Crown className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
            Welcome to Ultimate
          </span>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto text-center">
        <div className="rounded-3xl bg-gradient-to-br from-[#8B1A1A]/30 to-[#1F1B19] border border-[#454545] p-6 mb-6">
          <Monitor className="h-12 w-12 text-[#EEEAE3]" strokeWidth={1.5} />
        </div>

        <h1 className="text-3xl font-black uppercase tracking-tighter leading-tight text-[#EEEAE3] mb-3">
          Install
          <br />
          <span className="text-[#8B1A1A]">DailyHisaab Desktop</span>
        </h1>

        <p className="text-sm text-[#8A8A92] leading-relaxed mb-6">
          Your Ultimate plan unlocks full POS on Windows. Run billing, scanning, and printing on
          Desktop. Use this phone for reports, analysis, and staff admin.
        </p>

        <a
          href={DESKTOP_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] py-4 px-6 flex items-center justify-center gap-2 text-sm font-black uppercase tracking-wider text-white mb-3 transition-colors"
        >
          <Download className="h-4 w-4" />
          Download Desktop
          <ExternalLink className="h-3.5 w-3.5 opacity-70" />
        </a>

        <button
          onClick={handleSkip}
          className="w-full rounded-2xl border border-[#454545] bg-transparent py-3.5 px-6 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#8A8A92] hover:text-[#EEEAE3] transition-colors"
        >
          Continue to Admin Console
          <ArrowRight className="h-3.5 w-3.5" />
        </button>

        <p className="text-[10px] text-[#8A8A92] mt-6 leading-relaxed">
          Already installed Desktop? Sign in there — your devices will sync within seconds.
        </p>
      </div>
    </motion.div>
  );
}
